import { useEffect, useState, useContext } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Context } from "../../Reducers/Store";
import { formatarData, formatarDuracao, statusConfig } from "../../utils/deployStatus";

function saudacao() {
  const h = new Date().getHours();
  if (h < 12) return "Bom dia";
  if (h < 18) return "Boa tarde";
  return "Boa noite";
}

export default function Dashboard() {
  const [state] = useContext(Context);
  const navigate = useNavigate();
  const [dados, setDados] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios
      .get(`${process.env.REACT_APP_API}/deployment-history/dashboard`)
      .then((res) => setDados(res.data || null))
      .catch((err) => console.error(err))
      .finally(() => setLoading(false));
  }, []);

  const temDeployAtivo = (dados?.latestDeploys ?? []).some(
    (d) => d.lastStepStatus === "Running" || d.lastStepStatus === "Pending"
  );

  useEffect(() => {
    if (!temDeployAtivo) return;
    const interval = setInterval(() => {
      axios
        .get(`${process.env.REACT_APP_API}/deployment-history/dashboard`, {
          metadata: { ignoreLoading: true },
        })
        .then((res) => setDados(res.data || null))
        .catch(() => {});
    }, 5000);
    return () => clearInterval(interval);
  }, [temDeployAtivo]);

  const totalDeploys = dados?.totalDeploys ?? 0;
  const successfulDeploys = dados?.successfulDeploys ?? 0;
  const failedDeploys = dados?.failedDeploys ?? 0;
  const averageDuration = dados?.averageDuration;
  const latestDeploys = dados?.latestDeploys ?? [];

  const stats = [
    {
      titulo: "Total de Deploys",
      valor: loading ? "—" : totalDeploys,
      iconBg: "from-indigo-500 to-indigo-700",
      numColor: "text-indigo-600",
      icon: (
        <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v2a2 2 0 002 2h12a2 2 0 002-2v-2M12 12V4m0 0l-4 4m4-4l4 4" />
        </svg>
      ),
    },
    {
      titulo: "Com Sucesso",
      valor: loading ? "—" : successfulDeploys,
      iconBg: "from-emerald-500 to-emerald-700",
      numColor: "text-emerald-600",
      icon: (
        <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      ),
    },
    {
      titulo: "Com Falha",
      valor: loading ? "—" : failedDeploys,
      iconBg: "from-red-500 to-red-700",
      numColor: "text-red-600",
      icon: (
        <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01M4.93 19h14.14a2 2 0 001.74-3l-7.07-12a2 2 0 00-3.48 0l-7.07 12a2 2 0 001.74 3z" />
        </svg>
      ),
    },
    {
      titulo: "Duração Média",
      valor: loading ? "—" : formatarDuracao(averageDuration),
      iconBg: "from-violet-500 to-violet-700",
      numColor: "text-violet-600",
      icon: (
        <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      ),
    },
  ];

  return (
    <div className="max-w-6xl mx-auto">

      {/* Welcome banner */}
      <div
        className="rounded-2xl p-6 sm:p-8 mb-8 relative overflow-hidden"
        style={{ background: "linear-gradient(135deg, #1e1b4b 0%, #3b0764 100%)" }}
      >
        <div className="absolute -top-8 -right-8 w-48 h-48 rounded-full opacity-10" style={{ background: "radial-gradient(circle,#a78bfa,transparent)" }} />
        <div className="absolute -bottom-12 -left-6 w-40 h-40 rounded-full opacity-10" style={{ background: "radial-gradient(circle,#c4b5fd,transparent)" }} />

        <div className="relative flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <p className="text-purple-300 text-sm font-medium mb-1">{saudacao()},</p>
            <h1 className="text-2xl sm:text-3xl font-bold text-white mb-1">
              {state?.profile?.displayName || "Usuário"}! 👋
            </h1>
            <p className="text-purple-300 text-sm">Aqui está a visão geral dos seus deploys.</p>
          </div>
          <button
            onClick={() => navigate("/deploys/novo")}
            className="self-start sm:self-auto flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-semibold text-indigo-900 bg-white hover:bg-purple-50 transition shadow-lg shadow-purple-900/30 shrink-0"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M12 4v16m8-8H4" />
            </svg>
            Novo Deploy
          </button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        {stats.map((s) => (
          <div
            key={s.titulo}
            className="bg-white rounded-2xl p-5 shadow-sm ring-1 ring-slate-200/60 hover:shadow-md transition-shadow"
          >
            <div className="flex items-start justify-between mb-3">
              <div className={`p-2.5 rounded-xl bg-gradient-to-br ${s.iconBg} shadow-sm`}>
                {s.icon}
              </div>
            </div>
            <p className={`text-3xl font-extrabold ${s.numColor} leading-none mb-1`}>{s.valor}</p>
            <p className="text-xs font-medium text-slate-500">{s.titulo}</p>
          </div>
        ))}
      </div>

      {/* Últimos deploys */}
      <div className="bg-white rounded-2xl shadow-sm ring-1 ring-slate-200/60 overflow-hidden">
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100">
          <div className="flex items-center gap-2">
            <div className="w-2 h-2 rounded-full bg-violet-500" />
            <h2 className="text-base font-bold text-slate-800">Últimos Deploys</h2>
            {!loading && latestDeploys.length > 0 && (
              <span className="ml-1 px-2 py-0.5 text-xs font-semibold bg-violet-100 text-violet-700 rounded-full">
                {latestDeploys.length}
              </span>
            )}
          </div>
          <button
            onClick={() => navigate("/deploys")}
            className="text-sm font-medium text-violet-600 hover:text-violet-800 transition-colors"
          >
            Ver todos →
          </button>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-16">
            <div className="w-8 h-8 border-2 border-violet-300 border-t-violet-600 rounded-full animate-spin" />
          </div>
        ) : latestDeploys.length === 0 ? (
          <div className="text-center py-16 px-6">
            <div className="w-16 h-16 rounded-2xl bg-slate-100 flex items-center justify-center mx-auto mb-4">
              <svg className="w-8 h-8 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 16v2a2 2 0 002 2h12a2 2 0 002-2v-2M12 12V4m0 0l-4 4m4-4l4 4" />
              </svg>
            </div>
            <p className="text-slate-500 font-medium mb-1">Nenhum deploy registrado</p>
            <p className="text-slate-400 text-sm mb-4">Inicie seu primeiro deploy para acompanhar o histórico por aqui.</p>
            <button
              onClick={() => navigate("/deploys/novo")}
              className="px-5 py-2 text-sm font-semibold rounded-xl text-white transition"
              style={{ background: "linear-gradient(135deg,#8b5cf6,#6d28d9)" }}
            >
              Novo deploy
            </button>
          </div>
        ) : (
          <div className="divide-y divide-slate-100">
            {latestDeploys.map((deploy) => {
              const st = statusConfig(deploy.lastStepStatus || deploy.status);
              const target = deploy.target;
              const ambiente = target?.displayName || target?.siteName || "—";
              const autor = deploy.requestedBy?.displayName || deploy.requestedBy?.userName || "—";
              const observation = deploy.observation?.trim();

              return (
                <div
                  key={deploy.id}
                  onClick={() => navigate(`/deploys/${deploy.id}`)}
                  className="flex items-center gap-4 px-6 py-4 hover:bg-slate-50 cursor-pointer transition-colors group"
                >
                  {/* Status icon */}
                  <div
                    className={`w-10 h-10 rounded-xl flex items-center justify-center shrink-0 ${st.iconBg} ${st.iconColor}`}
                  >
                    {st.icon}
                  </div>

                  {/* Ambiente (primário) + branch + autor */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-0.5 flex-wrap">
                      <p className="font-bold text-slate-900 truncate group-hover:text-violet-700 transition-colors">
                        {ambiente}
                      </p>
                      {target?.isProduction && (
                        <span className="px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wider rounded bg-rose-100 text-rose-700 ring-1 ring-rose-200 shrink-0">
                          Prod
                        </span>
                      )}
                      {deploy.restoreDatabaseRequested && (
                        <span className="px-1.5 py-0.5 text-[10px] font-semibold rounded bg-violet-100 text-violet-700 ring-1 ring-violet-200 shrink-0">
                          BD restaurado
                        </span>
                      )}
                    </div>
                    <div className="flex items-center gap-2 text-xs text-slate-500 min-w-0">
                      <span className="flex items-center gap-1 font-mono text-slate-600 truncate">
                        <svg className="w-3 h-3 shrink-0 text-slate-400" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
                          <circle cx="6" cy="3" r="2" />
                          <circle cx="6" cy="21" r="2" />
                          <circle cx="18" cy="8" r="2" />
                          <path d="M6 5v14" />
                          <path d="M18 10v2a4 4 0 0 1-4 4H9" />
                        </svg>
                        <span className="truncate">{deploy.branchName}</span>
                      </span>
                      <span className="text-slate-300">·</span>
                      <span className="flex items-center gap-1 truncate">
                        <svg className="w-3 h-3 shrink-0 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                        </svg>
                        <span className="truncate">{autor}</span>
                      </span>
                    </div>
                    {observation && (
                      <p className="mt-1 text-xs text-slate-500 truncate">
                        {observation}
                      </p>
                    )}
                  </div>

                  {/* Duração + data */}
                  <div className="hidden sm:flex flex-col items-end gap-1 shrink-0 text-right">
                    <span className="text-xs font-semibold text-slate-600 tabular-nums">
                      {formatarDuracao(deploy.duration)}
                    </span>
                    <span className="text-[11px] text-slate-400">
                      {formatarData(deploy.finishedAt || deploy.startedAt || deploy.dataAgendamento || deploy.createdAt)}
                    </span>
                  </div>

                  {/* Badge de status */}
                  <span className={`hidden md:inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-semibold rounded-full shrink-0 ${st.badgeClass}`}>
                    <span className={`w-1.5 h-1.5 rounded-full ${st.dotClass}`} />
                    {st.label}
                  </span>

                  <svg className="w-4 h-4 text-slate-300 group-hover:text-violet-500 transition-colors shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
